import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Tooltip,
  Link,
  TextField,
  InputAdornment,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ListItemIcon,
} from '@mui/material';
import Fade from '@mui/material/Fade';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import PlayArrow from '../../Images/Play.png';
import Edit from '../../Images/Edit.png';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchFlowExecutionHistoryThunk,
  fetchWorkflowRunsByIdThunk,
  fetchWorkflowRunHistoryThunk,
} from '../../state/slices/dashboardSlice';
import {
  fetchWorkflowConfigAndSet,
  deleteWorkflow,
  clearWorkflow,
  fetchWorkflows,
} from '../../state/slices/workflowSlice';
import { updateSelectedEntitiesFromBackend } from '../../utils/EntityUtils';
import { removeAllEntitiesForWorkflow } from '../../state/slices/entitiesSlice';
import { deleteMappingForWorkflow } from '../../state/slices/mappingSlice';
import {
  verifyPasswordForReauth,
  verifyGoogleReauth,
  isGoogleUser,
} from '../../services/reauthService';
import WorkflowRunDialog from './WorflowRunDialog';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { useTimezone } from '../../hooks/useTimezone';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import VerifyCaptchaDialog from '../connectionPage/VerifyCaptchaDialog';

const statusColorMap = {
  Success: {
    bg: '#10B981',
    bgLight: '#D1FAE5',
    text: '#065F46',
    border: '#10B981',
  },
  Failed: {
    bg: '#EF4444',
    bgLight: '#FEE2E2',
    text: '#991B1B',
    border: '#EF4444',
  },
  'Partial Success': {
    bg: '#F59E0B',
    bgLight: '#FEF3C7',
    text: '#92400E',
    border: '#F59E0B',
  },
  SUCCESS: {
    bg: '#10B981',
    bgLight: '#D1FAE5',
    text: '#065F46',
    border: '#10B981',
  },
  FAILED: {
    bg: '#EF4444',
    bgLight: '#FEE2E2',
    text: '#991B1B',
    border: '#EF4444',
  },
  ACTIVE: {
    bg: '#3B82F6',
    bgLight: '#DBEAFE',
    text: '#1E40AF',
    border: '#3B82F6',
  },
  PARTIAL_SUCCESS: {
    bg: '#F59E0B',
    bgLight: '#FEF3C7',
    text: '#92400E',
    border: '#F59E0B',
  },
};

const FlowExecutionHistory = () => {
  const [showAll, setShowAll] = useState(false);
  const dispatch = useDispatch();
  const { formatDate } = useTimezone();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { workflowRuns = [], workflowRunsLoading } = useSelector(
    (state) => state.dashboard,
  );
  const { flowExecutionData = [], flowExecutionLoading } = useSelector(
    (state) => state.dashboard,
  );
  const { list: workflows } = useSelector((state) => state.workflows);
  const navigate = useNavigate();
  useEffect(() => {
    // Always fetch fresh execution history on mount
    dispatch(fetchFlowExecutionHistoryThunk());
  }, [dispatch]);

  useEffect(() => {
    const buildConnectionsMap = (data) => {
      if (!Array.isArray(data)) return;
      const connectionsMap = {};
      for (const run of data) {
        const wfId = run?.workflow_id;
        const meta = run?.metadata || {};
        const sources = Array.isArray(meta.sources) ? meta.sources : [];
        const destinations = Array.isArray(meta.destinations)
          ? meta.destinations
          : [];
        if (wfId) {
          connectionsMap[wfId] = { connections: { sources, destinations } };
        }
      }
      setWorkflowConnections(connectionsMap);
    };

    // Always fetch fresh run history on mount to build connection map
    dispatch(fetchWorkflowRunHistoryThunk({ range: 'weekly', limit: 100 }))
      .unwrap()
      .then((data) => buildConnectionsMap(data))
      .catch((e) => {
        console.error(
          'Failed to derive workflow connections from run-history:',
          e,
        );
      });
  }, [dispatch]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [pendingWorkflow, setPendingWorkflow] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  const [sourceConnectionFilter, setSourceConnectionFilter] = useState('');
  const [destinationConnectionFilter, setDestinationConnectionFilter] =
    useState('');
  const [executionStatusFilter, setExecutionStatusFilter] = useState('');
  const [runModeFilter, setRunModeFilter] = useState('');

  // Reauthentication state for workflow deletion
  const [step, setStep] = useState('confirm'); // 'confirm' -> 'reauth' -> 'verify'
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [reauthError, setReauthError] = useState('');
  const [reauthLoading, setReauthLoading] = useState(false);

  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: 'none',
  });

  const [workflowConnections, setWorkflowConnections] = useState({});

  useEffect(() => {
    if (isDeleteDialogOpen) {
      setStep('confirm');
      setPassword('');
      setShowPassword(false);
      setReauthError('');
      setReauthLoading(false);
    }
  }, [isDeleteDialogOpen]);

  const getWorkflowMeta = (workflowId) =>
    workflows.find((w) => w.workflow_id === workflowId) || null;

  const handleVerified = async () => {
    try {
      if (pendingAction === 'edit' && pendingWorkflow?.id) {
        await handleEdit(pendingWorkflow.id, { verified: true });
      } else if (pendingAction === 'run' && pendingWorkflow?.id) {
        await handleRun(pendingWorkflow.id, { verified: true });
      }
    } finally {
      setCaptchaOpen(false);
      setPendingWorkflow(null);
      setPendingAction(null);
    }
  };
  const handleClose = () => {
    setCaptchaOpen(false);
    setPendingWorkflow(null);
    setPendingAction(null);
  };

  // const truncateWorkflowId = (id, maxLength = 12) => {
  //   if (!id || id.length <= maxLength) return id;
  //   return `${id.substring(0, maxLength)}...`;
  // };

  const getConnectionNames = (workflowId) => {
    const connData = workflowConnections[workflowId];
    if (!connData || !connData.connections) {
      return { source: [], destination: [] };
    }

    const sources = connData.connections.sources || [];
    const sourceNames = sources.map((s) => s?.connection_name).filter(Boolean);

    const destinations = connData.connections.destinations || [];
    const destNames = destinations
      .map((d) => d?.connection_name)
      .filter(Boolean);

    return {
      source: sourceNames.length > 0 ? sourceNames : ['-'],
      destination: destNames.length > 0 ? destNames : ['-'],
    };
  };

  const filterOptions = useMemo(() => {
    const sourceConnections = new Set();
    const destinationConnections = new Set();
    const executionStatuses = new Set();
    const runModes = new Set();

    flowExecutionData.forEach((row) => {
      const sourceNames = getConnectionNames(row.workflow_id).source;
      sourceNames.forEach((name) => {
        if (name !== '-') sourceConnections.add(name);
      });

      const destNames = getConnectionNames(row.workflow_id).destination;
      destNames.forEach((name) => {
        if (name !== '-') destinationConnections.add(name);
      });

      if (row.run_status) {
        executionStatuses.add(row.run_status);
      }

      const runMode =
        row?.trigger_type === 'OnDemand'
          ? 'On Demand'
          : row?.trigger_type || 'On Demand';
      runModes.add(runMode);
    });

    return {
      sourceConnections: Array.from(sourceConnections).sort(),
      destinationConnections: Array.from(destinationConnections).sort(),
      executionStatuses: Array.from(executionStatuses).sort(),
      runModes: Array.from(runModes).sort(),
    };
  }, [flowExecutionData, workflowConnections]);

  const handleSort = (column) => {
    let direction = 'asc';

    if (sortConfig.column === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (
      sortConfig.column === column &&
      sortConfig.direction === 'desc'
    ) {
      direction = 'none';
      column = null;
    }

    setSortConfig({ column, direction });
  };

  const handleEdit = async (workflowId, { verified = false } = {}) => {
    try {
      // --- Guard: require an id
      if (!workflowId) {
        toast.error('Missing workflow id.');
        return;
      }

      // --- Gate: verification (captcha, etc.)
      if (!verified) {
        const wf = getWorkflowMeta(workflowId) || {};
        setPendingWorkflow({ id: workflowId, name: wf.name || '' });
        setPendingAction('edit');
        setCaptchaOpen(true);
        return;
      }

      const workflow = getWorkflowMeta(workflowId);
      if (!workflow) {
        toast.error('Workflow not found!');
        return;
      }

      // Normalize fields
      const id = workflow.workflow_id || workflow.id || workflowId;
      const name = (workflow.name || '').trim();
      const workflowName = name || String(id);

      // --- Fetch config and hydrate state
      try {
        const result = await dispatch(
          fetchWorkflowConfigAndSet({ workflowId: id, workflowName }),
        ).unwrap();

        if (result?.entitySelections) {
          updateSelectedEntitiesFromBackend(
            dispatch,
            id,
            result.entitySelections,
          );
        }
      } catch (err) {
        console.error('Error loading workflow configuration:', err);
      }

      navigate(`/parentworkflow/${encodeURIComponent(String(id))}`, {
        replace: false,
        state: { workflowName },
      });
    } catch (error) {
      console.error('Error in handleEdit:', error);
      toast.error('An error occurred while editing the workflow');
    }
  };

  const handleRun = async (workflowId, { verified = false } = {}) => {
    try {
      // --- Guard: require an id
      if (!workflowId) {
        toast.error('Missing workflow id.');
        return;
      }

      // --- Gate: verification (captcha, etc.)
      if (!verified) {
        const wf = getWorkflowMeta(workflowId) || {};
        setPendingWorkflow({ id: workflowId, name: wf.name || '' });
        setPendingAction('run');
        setCaptchaOpen(true);
        return;
      }

      // --- Resolve workflow meta (single source of truth)
      const wf = getWorkflowMeta(workflowId);
      if (!wf) {
        toast.error('Workflow not found');
        return;
      }

      // Normalize fields
      const id = wf.workflow_id || wf.id || workflowId;
      const name = (wf.name || '').trim();
      const workflowName = name || String(id);

      try {
        const result = await dispatch(
          fetchWorkflowConfigAndSet({ workflowId: id, workflowName }),
        ).unwrap();

        if (result?.entitySelections) {
          updateSelectedEntitiesFromBackend(
            dispatch,
            id,
            result.entitySelections,
          );
        }
      } catch (error) {
        console.error('Config load error:', error);
        // proceed regardless
      } finally {
        toast.success('Workflow is running now', { autoClose: 2000 });
        // Navigate using ID (stable), pass runNow to trigger execution
        navigate(`/parentworkflow/${encodeURIComponent(String(id))}`, {
          state: { runNow: true, workflowName },
        });
      }
    } catch (err) {
      console.error('Error in handleRun:', err);
      toast.error('An error occurred while starting the workflow');
    }
  };

  const handleDeleteClick = () => {
    if (isGoogleUser()) {
      handleGoogleReauth();
    } else {
      setStep('reauth');
    }
  };

  const handleGoogleReauth = async () => {
    try {
      setReauthLoading(true);
      setReauthError('');
      const result = await verifyGoogleReauth();
      if (result.success) {
        await performDelete();
      } else {
        setReauthError(result.error || 'Reauthentication failed');
      }
    } catch (error) {
      setReauthError('Reauthentication failed. Please try again.');
    } finally {
      setReauthLoading(false);
    }
  };

  const handlePasswordReauth = async () => {
    if (!password.trim()) {
      setReauthError('Please enter your password');
      return;
    }

    try {
      setReauthLoading(true);
      setReauthError('');
      const result = await verifyPasswordForReauth(password);
      if (result.success) {
        await performDelete();
      } else {
        setReauthError(result.error || 'Wrong password.');
      }
    } catch (error) {
      setReauthError('Reauthentication failed. Please try again.');
    } finally {
      setReauthLoading(false);
    }
  };

  const performDelete = async () => {
    try {
      if (!workflowToDelete) return;

      const workflow = workflows.find(
        (flow) => flow.workflow_id === workflowToDelete,
      );
      if (!workflow) {
        try {
          toast.error('Workflow not found!');
        } catch (toastError) {
          console.error('Toast error:', toastError);
        }
        return;
      }

      const workflowId = workflow.workflow_id;

      await dispatch(deleteWorkflow(workflowToDelete)).unwrap();
      dispatch(clearWorkflow(workflowId));
      dispatch(removeAllEntitiesForWorkflow(workflowId));
      dispatch(deleteMappingForWorkflow(workflowId));

      try {
        toast.success('Workflow deleted successfully', { autoClose: 2000 });
      } catch (toastError) {
        console.error('Toast error:', toastError);
      }

      setTimeout(() => {
        dispatch(fetchWorkflows());
        dispatch(fetchFlowExecutionHistoryThunk());
      }, 2200);
    } catch (error) {
      console.error('Error deleting workflow:', error);
      try {
        toast.error(error.message || 'Failed to delete workflow');
      } catch (toastError) {
        console.error('Toast error:', toastError);
      }
    } finally {
      setIsDeleteDialogOpen(false);
      setWorkflowToDelete(null);
    }
  };

  const handleWorkflowClick = (workflowId) => {
    setDialogOpen(true);
    dispatch(fetchWorkflowRunsByIdThunk(workflowId));
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.4,
        type: 'spring',
        stiffness: 100,
      },
    },
  };

  const rowVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: index * 0.05,
        duration: 0.3,
        ease: 'easeOut',
      },
    }),
    hover: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.2,
        ease: 'easeInOut',
      },
    },
  };

  // const displayedRows = showAll
  //   ? flowExecutionData
  //   : flowExecutionData.slice(0, 10);
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let filtered = flowExecutionData;

    if (q) {
      filtered = filtered.filter((r) =>
        r?.workflow_name?.toLowerCase().includes(q),
      );
    }

    if (sourceConnectionFilter) {
      filtered = filtered.filter((r) => {
        const sourceNames = getConnectionNames(r.workflow_id).source;
        return sourceNames.some((name) => name === sourceConnectionFilter);
      });
    }

    if (destinationConnectionFilter) {
      filtered = filtered.filter((r) => {
        const destNames = getConnectionNames(r.workflow_id).destination;
        return destNames.some((name) => name === destinationConnectionFilter);
      });
    }

    if (executionStatusFilter) {
      filtered = filtered.filter(
        (r) => r?.run_status === executionStatusFilter,
      );
    }

    if (runModeFilter) {
      filtered = filtered.filter((r) => {
        const runMode =
          r?.trigger_type === 'OnDemand'
            ? 'On Demand'
            : r?.trigger_type || 'On Demand';
        return runMode === runModeFilter;
      });
    }

    // Apply sorting
    if (sortConfig.direction !== 'none' && sortConfig.column) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.column) {
          case 'workflow_name':
            aValue = (a.workflow_name || '').toLowerCase();
            bValue = (b.workflow_name || '').toLowerCase();
            break;
          case 'execution_status':
            aValue = (a.run_status || '').toLowerCase();
            bValue = (b.run_status || '').toLowerCase();
            break;
          case 'run_mode':
            // Since run_mode might not exist, let's use a default value or check if it exists
            aValue = (a.run_mode || 'ON DEMAND').toLowerCase();
            bValue = (b.run_mode || 'ON DEMAND').toLowerCase();
            break;
          case 'last_run_at':
            aValue = a.last_run ? new Date(a.last_run).getTime() : 0;
            bValue = b.last_run ? new Date(b.last_run).getTime() : 0;
            break;
          case 'source':
            {
              const aSource = getConnectionNames(a.workflow_id).source;
              const bSource = getConnectionNames(b.workflow_id).source;
              aValue = (
                aSource[0] === '-' ? '' : aSource[0] || ''
              ).toLowerCase();
              bValue = (
                bSource[0] === '-' ? '' : bSource[0] || ''
              ).toLowerCase();
            }
            break;
          case 'destination':
            {
              const aDest = getConnectionNames(a.workflow_id).destination;
              const bDest = getConnectionNames(b.workflow_id).destination;
              aValue = (aDest[0] === '-' ? '' : aDest[0] || '').toLowerCase();
              bValue = (bDest[0] === '-' ? '' : bDest[0] || '').toLowerCase();
            }
            break;
          case 'workflow_id':
            aValue = (a.workflow_id || '').toLowerCase();
            bValue = (b.workflow_id || '').toLowerCase();
            break;
          default:
            return 0;
        }

        if (sortConfig.column === 'last_run_at') {
          // For dates, higher timestamp = more recent
          return sortConfig.direction === 'asc'
            ? aValue - bValue
            : bValue - aValue;
        } else {
          // For strings, alphabetical
          if (sortConfig.direction === 'asc') {
            return aValue.localeCompare(bValue);
          } else {
            return bValue.localeCompare(aValue);
          }
        }
      });
    }

    return filtered;
  }, [
    flowExecutionData,
    search,
    sortConfig,
    sourceConnectionFilter,
    destinationConnectionFilter,
    executionStatusFilter,
    runModeFilter,
    workflowConnections,
  ]);

  const displayedRows = showAll ? filteredRows : filteredRows.slice(0, 10);

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <Box
        component={motion.div}
        sx={{
          borderRadius: '12px',
          background: '#ffffff',
          border: '1px solid #E5E7EB',
          p: 0,
          mt: 1,
          mb: 2,
          position: 'relative',
          overflow: 'hidden',
          boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        }}
      >
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderBottom: '1px solid #E5E7EB',
              backgroundColor: '#ffffff',
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: '#111827',
                  mb: 0,
                }}
              >
                Flow Execution History
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                flexWrap: 'wrap',
              }}
            >
              {/* Search toggle / bar */}
              <AnimatePresence initial={false}>
                {searchOpen ? (
                  <motion.div
                    key="searchbar"
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: 280, opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{ type: 'tween', duration: 0.25 }}
                  >
                    <TextField
                      autoFocus
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search workflows..."
                      size="small"
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon
                              fontSize="small"
                              sx={{ color: '#6B7280' }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSearch('');
                                setSearchOpen(false);
                              }}
                              edge="end"
                            >
                              <CloseRounded sx={{ fontSize: 18 }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 34,
                          borderRadius: '8px',
                          background: '#F8FAFC',
                          fontSize: '0.75rem',
                        },
                      }}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="searchicon"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Tooltip title="Search workflows">
                      <IconButton
                        onClick={() => setSearchOpen(true)}
                        size="small"
                        sx={{
                          border: '1px solid rgba(79, 172, 254, 0.2)',
                          background: '#EEF2FF',
                          color: '#5965A6',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                          transition:
                            'transform .18s cubic-bezier(.2,.8,.2,1), box-shadow .2s ease, background-color .2s ease',
                          willChange: 'transform, box-shadow',
                          '&:hover': {
                            background: '#E5E9FF',
                            transform: 'translateY(-1px) scale(1.02)',
                            boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
                          },
                          '&:active': {
                            transform: 'translateY(0) scale(.99)',
                            boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
                          },
                          '&:focus-visible': {
                            outline: 'none',
                            boxShadow: '0 0 0 3px rgba(89,101,166,0.35)',
                          },
                        }}
                      >
                        <SearchIcon />
                      </IconButton>
                    </Tooltip>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* View All / View Less pill */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Typography
                  onClick={() => setShowAll(!showAll)}
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#5965A6', // keep same color as default
                    cursor: 'pointer',
                    px: 2,
                    py: 1,
                    borderRadius: '8px',
                    border: '1px solid rgba(79, 172, 254, 0.2)',
                    background: '#EEF2FF',
                    userSelect: 'none',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',

                    // match the Add New button’s animation feel
                    transition:
                      'box-shadow 200ms ease, outline-color 200ms ease',
                    willChange: 'box-shadow',

                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
                    },
                    '&:active': {
                      boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
                    },
                    '&:focus-visible': {
                      outline: 'none',
                      boxShadow: '0 0 0 3px rgba(89,101,166,0.35)',
                    },
                  }}
                >
                  {showAll ? 'View Less' : 'View All'}
                </Typography>
              </motion.div>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderBottom: '1px solid #E5E7EB',
              backgroundColor: '#ffffff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>
                  Filter by Source
                </InputLabel>
                <Select
                  value={sourceConnectionFilter}
                  onChange={(e) => setSourceConnectionFilter(e.target.value)}
                  label="Filter by Source"
                  IconComponent={KeyboardArrowDownRounded}
                  MenuProps={{
                    TransitionComponent: Fade,
                    PaperProps: {
                      sx: {
                        mt: 0.5,
                        borderRadius: '10px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
                        overflow: 'hidden',
                      },
                    },
                    MenuListProps: { dense: true, sx: { py: 0.5 } },
                  }}
                  sx={{
                    fontSize: '0.875rem',
                    borderRadius: '10px',
                    bgcolor: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e2e8f0',
                      transition: 'border-color .2s ease, box-shadow .2s ease',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#5965A6',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#8E98CD',
                      boxShadow: '0 0 0 2px rgba(89,101,166,0.14)',
                    },
                    '& .MuiSelect-select': {
                      py: 0.75,
                      px: 1.25,
                      fontSize: '0.75rem',
                    },
                    '& .MuiSelect-icon': {
                      color: '#94a3b8',
                      transition: 'transform .2s ease',
                    },
                    '&.Mui-expanded .MuiSelect-icon': {
                      transform: 'rotate(180deg)',
                    },
                    transition:
                      'box-shadow .2s ease, background-color .2s ease, border-color .2s ease',
                  }}
                >
                  <MenuItem
                    value=""
                    sx={{
                      fontSize: '0.875rem',
                      px: 1.25,
                      py: 0.75,
                      transition: 'background-color .15s ease',
                      '&:hover': { backgroundColor: '#F3F6FF' },
                      '&.Mui-selected, &.Mui-selected:hover': {
                        backgroundColor: '#EEF2FF',
                        color: '#5965A6',
                        fontWeight: 600,
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 24,
                        color: '#5965A6',
                        visibility:
                          sourceConnectionFilter === '' ? 'visible' : 'hidden',
                      }}
                    >
                      <CheckRounded fontSize="small" />
                    </ListItemIcon>
                    All Sources
                  </MenuItem>
                  {filterOptions.sourceConnections.map((conn) => (
                    <MenuItem
                      key={conn}
                      value={conn}
                      sx={{
                        fontSize: '0.875rem',
                        px: 1.25,
                        py: 0.75,
                        transition: 'background-color .15s ease',
                        '&:hover': { backgroundColor: '#F3F6FF' },
                        '&.Mui-selected, &.Mui-selected:hover': {
                          backgroundColor: '#EEF2FF',
                          color: '#5965A6',
                          fontWeight: 600,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 24,
                          color: '#5965A6',
                          visibility:
                            sourceConnectionFilter === conn
                              ? 'visible'
                              : 'hidden',
                        }}
                      >
                        <CheckRounded fontSize="small" />
                      </ListItemIcon>
                      {conn}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>
                  Filter by Destination
                </InputLabel>
                <Select
                  value={destinationConnectionFilter}
                  onChange={(e) =>
                    setDestinationConnectionFilter(e.target.value)
                  }
                  label="Filter by Destination"
                  IconComponent={KeyboardArrowDownRounded}
                  MenuProps={{
                    TransitionComponent: Fade,
                    PaperProps: {
                      sx: {
                        mt: 0.5,
                        borderRadius: '10px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
                        overflow: 'hidden',
                      },
                    },
                    MenuListProps: { dense: true, sx: { py: 0.5 } },
                  }}
                  sx={{
                    fontSize: '0.875rem',
                    borderRadius: '10px',
                    bgcolor: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e2e8f0',
                      transition: 'border-color .2s ease, box-shadow .2s ease',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#5965A6',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#8E98CD',
                      boxShadow: '0 0 0 2px rgba(89,101,166,0.14)',
                    },
                    '& .MuiSelect-select': {
                      py: 0.75,
                      px: 1.25,
                      fontSize: '0.75rem',
                    },
                    '& .MuiSelect-icon': {
                      color: '#94a3b8',
                      transition: 'transform .2s ease',
                    },
                    '&.Mui-expanded .MuiSelect-icon': {
                      transform: 'rotate(180deg)',
                    },
                    transition:
                      'box-shadow .2s ease, background-color .2s ease, border-color .2s ease',
                  }}
                >
                  <MenuItem
                    value=""
                    sx={{
                      fontSize: '0.875rem',
                      px: 1.25,
                      py: 0.75,
                      transition: 'background-color .15s ease',
                      '&:hover': { backgroundColor: '#F3F6FF' },
                      '&.Mui-selected, &.Mui-selected:hover': {
                        backgroundColor: '#EEF2FF',
                        color: '#5965A6',
                        fontWeight: 600,
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 24,
                        color: '#5965A6',
                        visibility:
                          destinationConnectionFilter === ''
                            ? 'visible'
                            : 'hidden',
                      }}
                    >
                      <CheckRounded fontSize="small" />
                    </ListItemIcon>
                    All Destinations
                  </MenuItem>
                  {filterOptions.destinationConnections.map((conn) => (
                    <MenuItem
                      key={conn}
                      value={conn}
                      sx={{
                        fontSize: '0.875rem',
                        px: 1.25,
                        py: 0.75,
                        transition: 'background-color .15s ease',
                        '&:hover': { backgroundColor: '#F3F6FF' },
                        '&.Mui-selected, &.Mui-selected:hover': {
                          backgroundColor: '#EEF2FF',
                          color: '#5965A6',
                          fontWeight: 600,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 24,
                          color: '#5965A6',
                          visibility:
                            destinationConnectionFilter === conn
                              ? 'visible'
                              : 'hidden',
                        }}
                      >
                        <CheckRounded fontSize="small" />
                      </ListItemIcon>
                      {conn}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>
                  Filter by Status
                </InputLabel>
                <Select
                  value={executionStatusFilter}
                  onChange={(e) => setExecutionStatusFilter(e.target.value)}
                  label="Filter by Status"
                  IconComponent={KeyboardArrowDownRounded}
                  MenuProps={{
                    TransitionComponent: Fade,
                    PaperProps: {
                      sx: {
                        mt: 0.5,
                        borderRadius: '10px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
                        overflow: 'hidden',
                      },
                    },
                    MenuListProps: { dense: true, sx: { py: 0.5 } },
                  }}
                  sx={{
                    fontSize: '0.875rem',
                    borderRadius: '10px',
                    bgcolor: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e2e8f0',
                      transition: 'border-color .2s ease, box-shadow .2s ease',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#5965A6',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#8E98CD',
                      boxShadow: '0 0 0 2px rgba(89,101,166,0.14)',
                    },
                    '& .MuiSelect-select': {
                      py: 0.75,
                      px: 1.25,
                      fontSize: '0.75rem',
                    },
                    '& .MuiSelect-icon': {
                      color: '#94a3b8',
                      transition: 'transform .2s ease',
                    },
                    '&.Mui-expanded .MuiSelect-icon': {
                      transform: 'rotate(180deg)',
                    },
                    transition:
                      'box-shadow .2s ease, background-color .2s ease, border-color .2s ease',
                  }}
                >
                  <MenuItem
                    value=""
                    sx={{
                      fontSize: '0.875rem',
                      px: 1.25,
                      py: 0.75,
                      transition: 'background-color .15s ease',
                      '&:hover': { backgroundColor: '#F3F6FF' },
                      '&.Mui-selected, &.Mui-selected:hover': {
                        backgroundColor: '#EEF2FF',
                        color: '#5965A6',
                        fontWeight: 600,
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 24,
                        color: '#5965A6',
                        visibility:
                          executionStatusFilter === '' ? 'visible' : 'hidden',
                      }}
                    >
                      <CheckRounded fontSize="small" />
                    </ListItemIcon>
                    All Statuses
                  </MenuItem>
                  {filterOptions.executionStatuses.map((status) => (
                    <MenuItem
                      key={status}
                      value={status}
                      sx={{
                        fontSize: '0.875rem',
                        px: 1.25,
                        py: 0.75,
                        transition: 'background-color .15s ease',
                        '&:hover': { backgroundColor: '#F3F6FF' },
                        '&.Mui-selected, &.Mui-selected:hover': {
                          backgroundColor: '#EEF2FF',
                          color: '#5965A6',
                          fontWeight: 600,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 24,
                          color: '#5965A6',
                          visibility:
                            executionStatusFilter === status
                              ? 'visible'
                              : 'hidden',
                        }}
                      >
                        <CheckRounded fontSize="small" />
                      </ListItemIcon>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>
                  Filter by Run Mode
                </InputLabel>
                <Select
                  value={runModeFilter}
                  onChange={(e) => setRunModeFilter(e.target.value)}
                  label="Filter by Run Mode"
                  IconComponent={KeyboardArrowDownRounded}
                  MenuProps={{
                    TransitionComponent: Fade,
                    PaperProps: {
                      sx: {
                        mt: 0.5,
                        borderRadius: '10px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
                        overflow: 'hidden',
                      },
                    },
                    MenuListProps: { dense: true, sx: { py: 0.5 } },
                  }}
                  sx={{
                    fontSize: '0.875rem',
                    borderRadius: '10px',
                    bgcolor: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e2e8f0',
                      transition: 'border-color .2s ease, box-shadow .2s ease',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#5965A6',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#8E98CD',
                      boxShadow: '0 0 0 2px rgba(89,101,166,0.14)',
                    },
                    '& .MuiSelect-select': {
                      py: 0.75,
                      px: 1.25,
                      fontSize: '0.75rem',
                    },
                    '& .MuiSelect-icon': {
                      color: '#94a3b8',
                      transition: 'transform .2s ease',
                    },
                    '&.Mui-expanded .MuiSelect-icon': {
                      transform: 'rotate(180deg)',
                    },
                    transition:
                      'box-shadow .2s ease, background-color .2s ease, border-color .2s ease',
                  }}
                >
                  <MenuItem
                    value=""
                    sx={{
                      fontSize: '0.875rem',
                      px: 1.25,
                      py: 0.75,
                      transition: 'background-color .15s ease',
                      '&:hover': { backgroundColor: '#F3F6FF' },
                      '&.Mui-selected, &.Mui-selected:hover': {
                        backgroundColor: '#EEF2FF',
                        color: '#5965A6',
                        fontWeight: 600,
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 24,
                        color: '#5965A6',
                        visibility: runModeFilter === '' ? 'visible' : 'hidden',
                      }}
                    >
                      <CheckRounded fontSize="small" />
                    </ListItemIcon>
                    All Modes
                  </MenuItem>
                  {filterOptions.runModes.map((mode) => (
                    <MenuItem
                      key={mode}
                      value={mode}
                      sx={{
                        fontSize: '0.875rem',
                        px: 1.25,
                        py: 0.75,
                        transition: 'background-color .15s ease',
                        '&:hover': { backgroundColor: '#F3F6FF' },
                        '&.Mui-selected, &.Mui-selected:hover': {
                          backgroundColor: '#EEF2FF',
                          color: '#5965A6',
                          fontWeight: 600,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 24,
                          color: '#5965A6',
                          visibility:
                            runModeFilter === mode ? 'visible' : 'hidden',
                        }}
                      >
                        <CheckRounded fontSize="small" />
                      </ListItemIcon>
                      {mode}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </motion.div>

        <TableContainer sx={{ backgroundColor: '#ffffff', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: '#5965A6' }}>
              <TableRow
                sx={{
                  '&:hover': {
                    backgroundColor: '#4C5B9A',
                  },
                  transition: 'background-color 0.2s ease',
                }}
              >
                <TableCell
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    py: 1,
                    px: 2,
                    color: '#ffffff',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #E5E7EB',
                    width: '60px',
                  }}
                >
                  Sr.No
                </TableCell>

                {/* Sortable headers */}
                {[
                  {
                    label: 'Workflow Name',
                    key: 'workflow_name',
                    sortable: true,
                  },
                  {
                    label: 'Execution Status',
                    key: 'execution_status',
                    sortable: true,
                  },
                  { label: 'Run Mode', key: 'run_mode', sortable: true },
                  { label: 'Last Run At', key: 'last_run_at', sortable: true },
                  { label: 'Source', key: 'source', sortable: true },
                  { label: 'Destination', key: 'destination', sortable: true },
                  // { label: 'Flow ID', key: 'workflow_id', sortable: true },
                ].map((header) => (
                  <TableCell
                    key={header.key}
                    onClick={
                      header.sortable ? () => handleSort(header.key) : undefined
                    }
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      py: 1,
                      px: 2,
                      color: '#ffffff',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #E5E7EB',
                      cursor: header.sortable ? 'pointer' : 'default',
                      userSelect: 'none',
                      position: 'relative',
                      '&:hover': header.sortable
                        ? {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            transition: 'background-color 0.2s ease',
                          }
                        : {},
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      {header.label}
                      {header.sortable &&
                        sortConfig.column === header.key &&
                        sortConfig.direction === 'asc' && (
                          <ArrowUpwardIcon
                            sx={{ fontSize: '0.875rem', ml: 0.5 }}
                          />
                        )}
                      {header.sortable &&
                        sortConfig.column === header.key &&
                        sortConfig.direction === 'desc' && (
                          <ArrowDownwardIcon
                            sx={{ fontSize: '0.875rem', ml: 0.5 }}
                          />
                        )}
                      {header.sortable &&
                        (!sortConfig.column ||
                          sortConfig.column !== header.key) && (
                          <UnfoldMoreIcon
                            sx={{ fontSize: '0.875rem', ml: 0.5, opacity: 0.5 }}
                          />
                        )}
                    </Box>
                  </TableCell>
                ))}

                {/* Actions header - not sortable */}
                <TableCell
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    py: 1,
                    px: 2,
                    color: '#ffffff',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #E5E7EB',
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {flowExecutionLoading ? (
                [...Array(5)].map((_, index) => (
                  <TableRow
                    key={index}
                    sx={{ borderBottom: '1px solid #F3F4F6' }}
                  >
                    <TableCell>
                      <Skeleton variant="text" width={20} height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={110} height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton
                        variant="rounded"
                        width={80}
                        height={24}
                        sx={{ borderRadius: '12px' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={100} height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={90} height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={90} height={20} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} height={20} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : displayedRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    align="center"
                    sx={{ py: 8, border: 'none' }}
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Typography
                        sx={{
                          color: '#9CA3AF',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                        }}
                      >
                        No execution history found.
                      </Typography>
                    </motion.div>
                  </TableCell>
                </TableRow>
              ) : (
                <AnimatePresence>
                  {displayedRows.map((row, index) => (
                    <TableRow
                      key={row.workflow_id}
                      component={motion.tr}
                      custom={index}
                      variants={rowVariants}
                      initial="hidden"
                      animate="visible"
                      whileHover="hover"
                      sx={{
                        borderBottom: '1px solid #F3F4F6',
                        backgroundColor:
                          index % 2 === 1 ? '#F3F5F7' : '#ffffff',
                        '&:hover': {
                          backgroundColor:
                            index % 2 === 1 ? '#71B9FF40' : '#71B9FF40',
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          py: 0.25,
                          px: 2,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: '#6B7280',
                        }}
                      >
                        {index + 1}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 0.25,
                          px: 2,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: '#111827',
                        }}
                      >
                        <Tooltip title={row.workflow_id} arrow placement="top">
                          <Link
                            component={motion.a}
                            onClick={() => handleWorkflowClick(row.workflow_id)}
                            sx={{
                              color: '#4C5B9A',
                              textDecoration: 'none',
                              fontWeight: 500,
                              cursor: 'pointer',
                              display: 'inline-block',
                              transformOrigin: 'left center',
                              willChange: 'transform, color',
                              backfaceVisibility: 'hidden',
                              WebkitFontSmoothing: 'antialiased',
                              MozOsxFontSmoothing: 'grayscale',
                              textRendering: 'optimizeLegibility',
                              textShadow: '0 0 0 currentColor',
                            }}
                            style={{
                              transformPerspective: 1000,
                            }}
                            whileHover={{ scaleX: 1.07, color: '#4C5B9A' }}
                            whileTap={{ scaleX: 1.04 }}
                            transition={{ duration: 0.18, ease: 'easeOut' }}
                          >
                            {row.workflow_name}
                          </Link>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            row.run_status === 'ACTIVE'
                              ? 'RUNNING'
                              : row.run_status || 'N/A'
                          }
                          size="small"
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            borderRadius: '16px',
                            height: 24,
                            minWidth: 80,
                            backgroundColor:
                              statusColorMap[row.run_status]?.bgLight ||
                              '#F3F4F6',
                            color:
                              statusColorMap[row.run_status]?.text || '#6B7280',
                            border: `1px solid ${
                              statusColorMap[row.run_status]?.border ||
                              '#D1D5DB'
                            }`,
                            textTransform: 'capitalize',
                            '& .MuiChip-label': {
                              px: 2.5,
                              py: 0.25,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 0.25,
                          px: 2,
                          fontSize: '0.875rem',
                          color: '#6b7280',
                          fontWeight: 400,
                          verticalAlign: 'middle',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'inline-block',
                            px: 1,
                            py: 0.2,
                            borderRadius: '8px',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            textWrap: 'nowrap',
                            textTransform: 'uppercase',
                            letterSpacing: '0.025em',
                            color: '#5965A6',
                          }}
                        >
                          {row?.trigger_type === 'OnDemand'
                            ? 'On Demand'
                            : (row?.trigger_type ?? '')}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 0.25,
                          px: 2,
                          fontSize: '0.875rem',
                          color: '#6B7280',
                          fontWeight: 400,
                        }}
                      >
                        {row.last_run
                          ? formatDate(row.last_run, { includeTime: true })
                          : '—'}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 0.25,
                          px: 2,
                          fontSize: '0.875rem',
                          color: '#6B7280',
                          fontWeight: 400,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                          }}
                        >
                          {getConnectionNames(row.workflow_id).source.map(
                            (name, idx) => (
                              <Typography
                                key={idx}
                                sx={{ fontSize: '0.875rem', color: '#6B7280' }}
                              >
                                {name}
                              </Typography>
                            ),
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 0.25,
                          px: 2,
                          fontSize: '0.875rem',
                          color: '#6B7280',
                          fontWeight: 400,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                          }}
                        >
                          {getConnectionNames(row.workflow_id).destination.map(
                            (name, idx) => (
                              <Typography
                                key={idx}
                                sx={{ fontSize: '0.875rem', color: '#6B7280' }}
                              >
                                {name}
                              </Typography>
                            ),
                          )}
                        </Box>
                      </TableCell>
                      {/* <TableCell
                        sx={{
                          py: 0.25,
                          px: 2,
                          fontSize: '0.875rem',
                          color: '#6B7280',
                          fontWeight: 400,
                        }}
                      >
                        <Tooltip title={row.workflow_id} arrow placement="top">
                          <span>{truncateWorkflowId(row.workflow_id)}</span>
                        </Tooltip>
                      </TableCell> */}
                      <TableCell>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Tooltip title="Run Workflow" arrow placement="top">
                            <motion.div>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRun(row.workflow_id);
                                }}
                              >
                                <img
                                  src={PlayArrow}
                                  alt="Play"
                                  style={{ width: 18, height: 18 }}
                                />
                              </IconButton>
                            </motion.div>
                          </Tooltip>
                          <Tooltip title="Edit Workflow" arrow placement="top">
                            <motion.div>
                              <IconButton
                                size="small"
                                // onClick={(e) => {
                                //   e.stopPropagation();
                                //   handleEdit(row.workflow_id);
                                // }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(row.workflow_id);
                                }}
                              >
                                <img
                                  src={Edit}
                                  alt="Edit"
                                  style={{ width: 22, height: 22 }}
                                />
                              </IconButton>
                            </motion.div>
                          </Tooltip>
                          {/* <Tooltip
                            title="Delete Workflow"
                            arrow
                            placement="top"
                          >
                            <motion.div>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(row.workflow_id);
                                }}
                              >
                                <img
                                  src={Delete}
                                  alt="Delete"
                                  style={{ width: 24, height: 24 }}
                                />
                              </IconButton>
                            </motion.div>
                          </Tooltip> */}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </AnimatePresence>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <WorkflowRunDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          data={workflowRuns}
          loading={workflowRunsLoading}
        />
        <Dialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>
            {step === 'confirm'
              ? 'Delete Workflow?'
              : 'Reauthenticate to Continue'}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {step === 'confirm' ? (
              <>
                <Typography variant="body1" sx={{ mb: 2, color: '#374151' }}>
                  Are you sure you want to delete this workflow? This action
                  cannot be undone.
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  You will need to reauthenticate to confirm this action.
                </Typography>
              </>
            ) : (
              <>
                <Typography sx={{ mb: 2, color: '#374151', fontSize: '1rem' }}>
                  Reauthenticate to Continue
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, color: '#6b7280' }}>
                  Please enter your password to confirm deletion of this
                  workflow.
                </Typography>
                <Box sx={{ width: '100%', maxWidth: 300 }}>
                  <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    error={!!reauthError}
                    helperText={reauthError}
                    disabled={reauthLoading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            disabled={reauthLoading}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                    }}
                  />
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            {step === 'confirm' ? (
              <>
                <Button
                  onClick={() => setIsDeleteDialogOpen(false)}
                  variant="outlined"
                  sx={{
                    minWidth: 100,
                    height: 40,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteClick}
                  variant="contained"
                  disabled={reauthLoading}
                  startIcon={
                    reauthLoading ? <CircularProgress size={16} /> : null
                  }
                  sx={{
                    minWidth: 160,
                    height: 40,
                    borderRadius: 1,
                    px: 4,
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: 'none',
                    fontSize: '0.95rem',
                    backgroundImage: 'linear-gradient(90deg, #de1d0f, #f87f5d)',
                    color: '#fff',
                    '&:hover': {
                      backgroundImage:
                        'linear-gradient(90deg, #e61405, #de1d0f)',
                      boxShadow: '0 8px 20px rgba(79, 172, 254, 0.25)',
                      transform: 'translateY(-1px)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                      boxShadow: 'none',
                    },
                    transition: 'all .18s ease',
                    opacity: reauthLoading ? 0.85 : 1,
                  }}
                >
                  {reauthLoading ? 'Verifying…' : 'Delete Workflow'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => setStep('confirm')}
                  variant="outlined"
                  disabled={reauthLoading}
                  sx={{
                    minWidth: 100,
                    height: 40,
                    borderRadius: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Back
                </Button>
                <Button
                  onClick={handlePasswordReauth}
                  variant="contained"
                  disabled={reauthLoading || !password.trim()}
                  startIcon={
                    reauthLoading ? <CircularProgress size={16} /> : null
                  }
                  sx={{
                    minWidth: 160,
                    height: 40,
                    borderRadius: 1,
                    px: 4,
                    fontWeight: 700,
                    textTransform: 'none',
                    boxShadow: 'none',
                    fontSize: '0.95rem',
                    backgroundImage: 'linear-gradient(90deg, #de1d0f, #f87f5d)',
                    color: '#fff',
                    '&:hover': {
                      backgroundImage:
                        'linear-gradient(90deg, #e61405, #de1d0f)',
                      boxShadow: '0 8px 20px rgba(79, 172, 254, 0.25)',
                      transform: 'translateY(-1px)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                      boxShadow: 'none',
                    },
                    transition: 'all .18s ease',
                    opacity: reauthLoading || !password.trim() ? 0.85 : 1,
                  }}
                >
                  {reauthLoading ? 'Verifying…' : 'Verify Password'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
        <VerifyCaptchaDialog
          open={captchaOpen}
          onClose={handleClose}
          onVerified={handleVerified}
          title="Quick verification"
          description={`Please complete the check to ${
            pendingAction === 'run' ? 'run' : 'edit'
          } "${pendingWorkflow?.name || 'this workflow'}".`}
        />
      </Box>
    </motion.div>
  );
};

export default FlowExecutionHistory;
